import React from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { formatCurrency, convertUSDTo } from '@/lib/currency'

interface CostDataPoint {
  day: string
  total_cost_usd: number
}

interface CostChartProps {
  data: CostDataPoint[]
  currency?: string
  height?: number
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  currency: string
}

function CustomTooltip({ active, payload, label, currency }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null
  const value = payload[0]?.value ?? 0

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-xs font-medium text-gray-500 mb-1">{label ? formatDate(label) : ''}</p>
      <p className="text-sm font-semibold text-violet-700">
        {formatCurrency(value, currency)}
      </p>
    </div>
  )
}

export function CostChart({ data, currency = 'PHP', height = 300 }: CostChartProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        No cost data available
      </div>
    )
  }

  const convertedData = data.map(d => ({
    ...d,
    cost: convertUSDTo(d.total_cost_usd, currency),
  }))

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={convertedData}
        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
      >
        <defs>
          <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.9} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.7} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={formatDate}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v) => formatCurrency(v / (currency !== 'USD' ? 1 : 1), currency).replace(/\.\d+$/, '')}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          axisLine={false}
          tickLine={false}
          width={56}
        />
        <Tooltip
          content={(props) => (
            <CustomTooltip
              {...(props as TooltipProps<number, string>)}
              currency={currency}
            />
          )}
        />
        <Bar
          dataKey="cost"
          fill="url(#costGradient)"
          radius={[4, 4, 0, 0]}
          maxBarSize={40}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}
